import { useState, useRef, useEffect } from 'react';
import { ChevronRight, CheckCircle, Terminal, Book, FileText, ChevronDown, ShieldCheck } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const DOCS_PDF_URL = `https://docs.google.com/viewer?url=${encodeURIComponent(`${BACKEND_URL}/api/docs`)}`;
const COMPLIANCE_PDF_URL = `https://docs.google.com/viewer?url=${encodeURIComponent(`${BACKEND_URL}/api/compliance`)}`;

const onboardingSteps = [
  {
    step: '01',
    title: 'Partnership Agreement',
    duration: '1–2 Days',
    description: 'Execute the NexGenPay white-label partnership agreement. Covers licensing, SLA, data ownership, and revenue sharing terms. Your legal team reviews a standard bank-friendly MSA.',
    items: ['Master Services Agreement (MSA)', 'Data Ownership Addendum', 'Revenue Share Schedule', 'SLA & Uptime Guarantee (99.9%)'],
  },
  {
    step: '02',
    title: 'Brand Configuration',
    duration: '1 Day',
    description: 'Submit your brand assets — logo, colors, app name, and domain. NexGenPay configures your white-label instance. Your customers see 100% your bank\'s brand, zero NexGenPay attribution.',
    items: ['Logo & App Icon Upload', 'Color Palette Configuration', 'Custom Domain / App Name', 'App Store Listing Setup (optional)'],
  },
  {
    step: '03',
    title: 'Core System Integration',
    duration: '1–3 Days',
    description: 'Connect NexGenPay to your core banking system via our pre-built connectors. We support Fiserv, FIS, Jack Henry, Finastra, and open API cores. FedNow routing numbers configured here.',
    items: ['Core Banking Connector Setup', 'FedNow Routing Number Config', 'ACH / RTP Fallback Setup', 'Webhook Endpoint Registration'],
  },
  {
    step: '04',
    title: 'Compliance & KYC Setup',
    duration: '1–2 Days',
    description: 'Configure your KYC/AML thresholds, BSA reporting rules, and OFAC screening. NexGenPay is BSA/AML-ready out of the box — you set the parameters, we enforce them automatically.',
    items: ['KYC Tier Configuration', 'AML / BSA Rule Setup', 'OFAC Screening Enable', 'Transaction Monitoring Alerts'],
  },
  {
    step: '05',
    title: 'Sandbox Testing',
    duration: '1–2 Days',
    description: 'Your team gets a fully isolated sandbox environment with test accounts, dummy FedNow transfers, and simulated merchant QR pay flows. Validate every user flow before going live.',
    items: ['Test Wallet Accounts', 'Simulated FedNow Transfers', 'QR Pay Test Merchant', 'Cardless ATM Simulation'],
  },
  {
    step: '06',
    title: 'Go-Live',
    duration: 'Day 7+',
    description: 'Flip the switch. Your branded wallet goes live for customers. NexGenPay monitors uptime, processes FedNow transfers, and provides real-time dashboards for your operations team.',
    items: ['Production Environment Deploy', 'Customer Onboarding Activation', 'Operations Dashboard Access', 'Dedicated Support Channel'],
  },
];

const apiEndpoints = [
  {
    method: 'POST',
    path: '/v1/wallets',
    description: 'Create a new customer wallet',
    body: `{\n  "customer_id": "cust_123",\n  "phone": "+15559876543",\n  "kyc_tier": 1\n}`,
    response: `{\n  "wallet_id": "wlt_abc123",\n  "status": "active",\n  "balance": 0.00\n}`,
  },
  {
    method: 'POST',
    path: '/v1/transfers/fednow',
    description: 'Send instant FedNow payment',
    body: `{\n  "from_wallet": "wlt_abc123",\n  "to_routing": "021000089",\n  "to_account": "00112233",\n  "amount": 250.00,\n  "memo": "Payroll"\n}`,
    response: `{\n  "transfer_id": "txn_xyz789",\n  "status": "settled",\n  "settled_at": "2025-01-15T09:00:00Z"\n}`,
  },
  {
    method: 'GET',
    path: '/v1/wallets/{wallet_id}/balance',
    description: 'Retrieve wallet balance',
    body: null,
    response: `{\n  "wallet_id": "wlt_abc123",\n  "available": 2450.00,\n  "pending": 0.00,\n  "currency": "USD"\n}`,
  },
  {
    method: 'POST',
    path: '/v1/qr/generate',
    description: 'Generate merchant QR code for payment acceptance',
    body: `{\n  "merchant_id": "mch_456",\n  "amount": 12.50,\n  "memo": "Coffee"\n}`,
    response: `{\n  "qr_code": "data:image/png;base64,...",\n  "expires_at": "2025-01-15T09:05:00Z"\n}`,
  },
  {
    method: 'POST',
    path: '/v1/webhooks',
    description: 'Register a webhook endpoint',
    body: `{\n  "url": "https://yourbank.com/webhook",\n  "events": ["transfer.settled", "wallet.created"]\n}`,
    response: `{\n  "webhook_id": "wh_001",\n  "status": "active"\n}`,
  },
];

const TABS = ['Onboarding Guide', 'API Reference', 'Technical Docs', 'Compliance & Security'];

export default function DocsSection() {
  const sectionRef = useRef(null);
  const [revealed, setRevealed] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [expandedStep, setExpandedStep] = useState(0);
  const [activeEndpoint, setActiveEndpoint] = useState(0);

  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setRevealed(true); obs.disconnect(); } },
      { threshold: 0.05 }
    );
    if (sectionRef.current) obs.observe(sectionRef.current);
    return () => obs.disconnect();
  }, []);

  return (
    <section
      id="docs"
      data-testid="docs-section"
      ref={sectionRef}
      className="relative section-padding"
      style={{ background: 'linear-gradient(180deg, #000 0%, #030803 50%, #000 100%)', scrollMarginTop: '80px' }}
    >
      {/* Glow */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div className="w-[800px] h-[500px]" style={{ background: 'radial-gradient(ellipse, rgba(0,255,0,0.04), transparent 70%)' }} />
      </div>

      <div className="max-w-7xl mx-auto px-6 relative">
        {/* Header */}
        <div
          className="mb-10"
          style={{ opacity: revealed ? 1 : 0, transform: revealed ? 'translateY(0)' : 'translateY(30px)', transition: 'all 0.7s ease' }}
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-[2px] bg-[#00FF00]" />
            <span className="text-[#00FF00] font-mono text-[11px] uppercase tracking-[0.4em]">Developer & Bank Resources</span>
          </div>
          <h2 className="text-4xl sm:text-5xl font-black uppercase text-white leading-none"
            style={{ fontFamily: 'Unbounded, sans-serif', letterSpacing: '-0.02em' }}>
            Documentation
            <br /><span style={{ color: '#00FF00', textShadow: '0 0 30px rgba(0,255,0,0.3)' }}>& Guides</span>
          </h2>
        </div>

        {/* Tab bar */}
        <div
          className="flex border-b border-[#1A1A1A] mb-8 overflow-x-auto"
          style={{ opacity: revealed ? 1 : 0, transition: 'opacity 0.7s ease 0.1s' }}
        >
          {TABS.map((tab, i) => (
            <button
              key={tab}
              onClick={() => setActiveTab(i)}
              data-testid={`docs-tab-${i}`}
              className="flex items-center gap-2 px-6 py-4 text-xs font-bold uppercase tracking-widest whitespace-nowrap transition-all duration-200 border-b-2 -mb-px"
              style={{
                fontFamily: 'Unbounded, sans-serif',
                color: activeTab === i ? '#00FF00' : '#A3A3A3',
                borderBottomColor: activeTab === i ? '#00FF00' : 'transparent',
                background: 'transparent',
              }}
            >
              {i === 0 && <Book size={13} />}
              {i === 1 && <Terminal size={13} />}
              {i === 2 && <FileText size={13} />}
              {i === 3 && <ShieldCheck size={13} />}
              {tab}
            </button>
          ))}
        </div>

        {/* Tab 0: Onboarding Guide */}
        {activeTab === 0 && (
          <div className="flex flex-col gap-2" style={{ animation: 'fadeIn 0.4s ease' }}>
            {onboardingSteps.map((s, i) => (
              <div
                key={s.step}
                className="border border-[#1A1A1A] overflow-hidden"
                style={{
                  background: expandedStep === i ? '#0A0A0A' : 'transparent',
                  borderColor: expandedStep === i ? 'rgba(0,255,0,0.25)' : '#1A1A1A',
                  transition: 'all 0.3s ease',
                }}
              >
                <button
                  className="w-full flex items-center justify-between p-5 text-left"
                  onClick={() => setExpandedStep(expandedStep === i ? -1 : i)}
                  data-testid={`onboarding-step-${i}`}
                >
                  <div className="flex items-center gap-4">
                    <span className="font-mono text-2xl font-black opacity-30 flex-shrink-0"
                      style={{ color: expandedStep === i ? '#00FF00' : '#fff', fontFamily: 'JetBrains Mono, monospace' }}>
                      {s.step}
                    </span>
                    <div>
                      <div className="text-white font-bold text-sm uppercase"
                        style={{ fontFamily: 'Unbounded, sans-serif', fontSize: '12px' }}>
                        {s.title}
                      </div>
                      <div className="text-[#00FF00] font-mono text-[10px] mt-0.5">{s.duration}</div>
                    </div>
                  </div>
                  <ChevronDown
                    size={16}
                    className="text-[#A3A3A3] flex-shrink-0 transition-transform duration-300"
                    style={{ transform: expandedStep === i ? 'rotate(180deg)' : 'rotate(0deg)' }}
                  />
                </button>
                {expandedStep === i && (
                  <div className="px-5 pb-5 border-t border-[#1A1A1A]" style={{ animation: 'fadeIn 0.3s ease' }}>
                    <p className="text-[#A3A3A3] text-sm leading-relaxed mt-4 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      {s.description}
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {s.items.map(item => (
                        <div key={item} className="flex items-center gap-2">
                          <CheckCircle size={13} className="text-[#00FF00] flex-shrink-0" />
                          <span className="text-[#A3A3A3] text-xs" style={{ fontFamily: 'Manrope, sans-serif' }}>{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Tab 1: API Reference */}
        {activeTab === 1 && (
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-0 border border-[#1A1A1A]" style={{ animation: 'fadeIn 0.4s ease' }}>
            {/* Left: endpoint list */}
            <div className="lg:col-span-2 border-b lg:border-b-0 lg:border-r border-[#1A1A1A]">
              <div className="p-4 border-b border-[#1A1A1A]">
                <div className="text-[#00FF00] font-mono text-[10px] uppercase tracking-widest">Base URL</div>
                <div className="text-white font-mono text-xs mt-1">https://api.nexgenpay.co/v1</div>
              </div>
              {apiEndpoints.map((ep, i) => (
                <button
                  key={i}
                  onClick={() => setActiveEndpoint(i)}
                  data-testid={`api-endpoint-${i}`}
                  className="w-full text-left p-4 border-b border-[#1A1A1A] last:border-b-0 transition-all duration-200"
                  style={{
                    background: activeEndpoint === i ? 'rgba(0,255,0,0.05)' : 'transparent',
                    borderLeft: activeEndpoint === i ? '3px solid #00FF00' : '3px solid transparent',
                  }}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-mono text-[10px] font-bold px-2 py-0.5"
                      style={{
                        background: ep.method === 'GET' ? 'rgba(0,255,0,0.1)' : 'rgba(0,150,255,0.1)',
                        color: ep.method === 'GET' ? '#00FF00' : '#4A9EFF',
                        border: `1px solid ${ep.method === 'GET' ? 'rgba(0,255,0,0.3)' : 'rgba(74,158,255,0.3)'}`,
                      }}>
                      {ep.method}
                    </span>
                  </div>
                  <div className="font-mono text-[11px] text-white">{ep.path}</div>
                  <div className="text-[#A3A3A3] text-[10px] mt-1" style={{ fontFamily: 'Manrope, sans-serif' }}>{ep.description}</div>
                </button>
              ))}
            </div>

            {/* Right: code panel */}
            <div className="lg:col-span-3" style={{ background: '#080808' }}>
              <div className="p-6">
                <div className="flex items-center gap-2 mb-4">
                  <span className="font-mono text-[10px] font-bold px-2 py-0.5"
                    style={{
                      background: apiEndpoints[activeEndpoint].method === 'GET' ? 'rgba(0,255,0,0.1)' : 'rgba(0,150,255,0.1)',
                      color: apiEndpoints[activeEndpoint].method === 'GET' ? '#00FF00' : '#4A9EFF',
                      border: `1px solid ${apiEndpoints[activeEndpoint].method === 'GET' ? 'rgba(0,255,0,0.3)' : 'rgba(74,158,255,0.3)'}`,
                    }}>
                    {apiEndpoints[activeEndpoint].method}
                  </span>
                  <span className="font-mono text-sm text-white">{apiEndpoints[activeEndpoint].path}</span>
                </div>

                {apiEndpoints[activeEndpoint].body && (
                  <div className="mb-4">
                    <div className="text-[#A3A3A3] font-mono text-[10px] uppercase tracking-widest mb-2">Request Body</div>
                    <pre className="p-4 overflow-x-auto text-xs leading-relaxed"
                      style={{ background: '#0D0D0D', border: '1px solid #1A1A1A', color: '#00FF00', fontFamily: 'JetBrains Mono, monospace' }}>
                      {apiEndpoints[activeEndpoint].body}
                    </pre>
                  </div>
                )}

                <div>
                  <div className="text-[#A3A3A3] font-mono text-[10px] uppercase tracking-widest mb-2">Response</div>
                  <pre className="p-4 overflow-x-auto text-xs leading-relaxed"
                    style={{ background: '#0D0D0D', border: '1px solid #1A1A1A', color: '#A3A3A3', fontFamily: 'JetBrains Mono, monospace' }}>
                    {apiEndpoints[activeEndpoint].response}
                  </pre>
                </div>

                <div className="mt-6 p-4 border border-[#1A1A1A]" style={{ background: '#0A0A0A' }}>
                  <div className="text-[#00FF00] font-mono text-[10px] uppercase tracking-widest mb-2">Authentication</div>
                  <div className="font-mono text-xs text-[#A3A3A3]">
                    Authorization: Bearer <span className="text-[#00FF00]">{'<your_api_key>'}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tab 2: Technical Docs */}
        {activeTab === 2 && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" style={{ animation: 'fadeIn 0.4s ease' }}>
            <a
              href={DOCS_PDF_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="docs-technical-pdf-link"
              className="group border border-[#1A1A1A] p-8 flex flex-col gap-4 hover:border-[#00FF00] transition-all duration-300"
              style={{ background: '#0A0A0A' }}
            >
              <div className="p-3 border border-[#1A1A1A] w-fit group-hover:border-[#00FF00] transition-colors duration-300"
                style={{ background: 'rgba(0,255,0,0.05)' }}>
                <FileText size={24} className="text-[#00FF00]" />
              </div>
              <div>
                <div className="text-white font-bold text-sm uppercase mb-1" style={{ fontFamily: 'Unbounded, sans-serif', fontSize: '12px' }}>
                  Technical Documentation
                </div>
                <div className="text-[#A3A3A3] text-sm leading-relaxed" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Full platform technical spec — architecture, FedNow integration details, security model, core connectors, and deployment guide.
                </div>
              </div>
              <div className="flex items-center gap-2 text-[#00FF00] font-mono text-xs uppercase tracking-widest mt-auto">
                View Document <ChevronRight size={12} />
              </div>
            </a>

            <div className="border border-[#1A1A1A] p-8 flex flex-col gap-4" style={{ background: '#0A0A0A' }}>
              <div className="p-3 border border-[#1A1A1A] w-fit" style={{ background: 'rgba(0,255,0,0.05)' }}>
                <Terminal size={24} className="text-[#00FF00]" />
              </div>
              <div>
                <div className="text-white font-bold text-sm uppercase mb-1" style={{ fontFamily: 'Unbounded, sans-serif', fontSize: '12px' }}>
                  Sandbox Environment
                </div>
                <div className="text-[#A3A3A3] text-sm leading-relaxed" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Request access to a fully isolated sandbox with test wallets, simulated FedNow transfers, and mock merchant QR flows. Ready in 24 hours.
                </div>
              </div>
              <button
                onClick={() => document.querySelector('#sandbox')?.scrollIntoView({ behavior: 'smooth' })}
                className="flex items-center gap-2 text-[#00FF00] font-mono text-xs uppercase tracking-widest mt-auto hover:gap-3 transition-all duration-200"
              >
                Request Sandbox Access <ChevronRight size={12} />
              </button>
            </div>
          </div>
        )}

        {/* Tab 3: Compliance & Security */}
        {activeTab === 3 && (
          <div style={{ animation: 'fadeIn 0.4s ease' }}>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
              {[
                { label: 'SOC 2 Type II', sub: 'In progress', note: 'Annual audit cycle' },
                { label: 'BSA / AML Ready', sub: 'Compliant', note: 'Bank Secrecy Act aligned' },
                { label: 'OFAC Screening', sub: 'Active', note: 'Real-time sanctions check' },
                { label: 'PCI DSS', sub: 'Compliant', note: 'Level 1 service provider' },
                { label: 'FDIC Part 364', sub: 'Aligned', note: 'IT safety & soundness standards' },
                { label: 'FFIEC Guidelines', sub: 'Aligned', note: 'Federal exam readiness' },
              ].map((badge) => (
                <div key={badge.label} className="border border-[#1A1A1A] p-5 flex items-start gap-4"
                  style={{ background: '#0A0A0A' }}>
                  <ShieldCheck size={18} className="text-[#00FF00] flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="text-white font-bold text-xs uppercase" style={{ fontFamily: 'Unbounded, sans-serif', fontSize: '11px' }}>{badge.label}</div>
                    <div className="text-[#00FF00] font-mono text-[10px] mt-0.5">{badge.sub}</div>
                    <div className="text-[#A3A3A3] text-[11px] mt-1" style={{ fontFamily: 'Manrope, sans-serif' }}>{badge.note}</div>
                  </div>
                </div>
              ))}
            </div>

            <a
              href={COMPLIANCE_PDF_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="docs-compliance-pdf-link"
              className="group border border-[#1A1A1A] p-8 flex flex-col sm:flex-row items-start sm:items-center gap-6 hover:border-[#00FF00] transition-all duration-300"
              style={{ background: '#0A0A0A' }}
            >
              <div className="p-4 border border-[#1A1A1A] w-fit flex-shrink-0 group-hover:border-[#00FF00] transition-colors duration-300"
                style={{ background: 'rgba(0,255,0,0.05)' }}>
                <ShieldCheck size={28} className="text-[#00FF00]" />
              </div>
              <div className="flex-1">
                <div className="text-white font-bold text-sm uppercase mb-1" style={{ fontFamily: 'Unbounded, sans-serif', fontSize: '12px' }}>
                  NexGenPay Compliance & Security Posture
                </div>
                <div className="text-[#A3A3A3] text-sm leading-relaxed" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Full compliance framework, security architecture, regulatory alignment, and audit readiness documentation. Designed for bank compliance officers and examiners.
                </div>
              </div>
              <div className="flex items-center gap-2 text-[#00FF00] font-mono text-xs uppercase tracking-widest flex-shrink-0 group-hover:gap-3 transition-all duration-200">
                View Document <ChevronRight size={12} />
              </div>
            </a>
          </div>
        )}
      </div>

      <style>{`@keyframes fadeIn { from { opacity:0; transform:translateY(8px); } to { opacity:1; transform:translateY(0); } }`}</style>
    </section>
  );
}
